import { DEFAULT_PART_DATE_MASK, formatPartDate } from '@inference.engine/taxonomy/web';
import { type TaxonomyField, type TrackingCodePart } from '@/entities/taxonomy';

/** Human token for one pattern part (used in the preview chips / string). */
export function describePart(part: TrackingCodePart, fieldName?: string): string {
  switch (part.kind) {
    case 'field':
      // eslint-disable-next-line i18next/no-literal-string -- pattern placeholder tokens
      return `{${fieldName ?? 'field'}${part.source === 'value' ? ':value' : ''}}`;
    case 'literal':
      return part.text ?? '';
    case 'date': {
      // A cleared mask advertises NOTHING, because the generator emits nothing
      // for it. `{}` was the previous answer and was worse than `{YYYY}`: it
      // still occupied a chip, so it read as a token that renders empty rather
      // than as a part contributing no characters at all.
      const mask = part.format ?? DEFAULT_PART_DATE_MASK;
      return mask ? `{${mask}}` : '';
    }
    case 'sequence':
      return '#'.repeat(Math.max(1, part.length ?? 5));
    case 'random':
      return '?'.repeat(Math.max(1, part.length ?? 6));
    default:
      return '';
  }
}

/** Renders a pattern to a readable template string, e.g. `{Trademark}-{Brand}-{YYYY}-#####`. */
export function patternToString(pattern: TrackingCodePart[], fields: TaxonomyField[]): string {
  const nameById = new Map(fields.map((f) => [f.id, f.name]));
  return pattern
    .map((p) => describePart(p, p.fieldId ? nameById.get(p.fieldId) : undefined))
    .join('');
}

/**
 * Resolves a computed field's pattern for ONE builder row — the live preview of
 * the generated value using the schema's own rules: field parts emit the
 * referenced field's code (or value) at that row, date parts the current date,
 * and the sequence its `#####` placeholder (the real unique number is allocated
 * server-side at generation, not per vocabulary row). Returns '' when the row
 * has nothing to resolve.
 */
export function resolveVariableForRow(
  field: TaxonomyField,
  fields: TaxonomyField[],
  row: number,
): string {
  const pattern = field.pattern ?? [];
  if (pattern.length === 0) return '';
  const byId = new Map(fields.map((f) => [f.id, f]));
  let hasResolvedPart = false;
  const out = pattern
    .map((part) => {
      switch (part.kind) {
        case 'literal':
          return part.text ?? '';
        case 'date':
          hasResolvedPart = true;
          // The generator's own renderer, so a cleared mask previews the empty
          // string it will actually mint. Routing this through the FIELD helper
          // was wrong by eight characters: that one falls back on blank as well
          // as on unset, because a date picker cannot render nothing.
          return formatPartDate(new Date(), part.format);
        case 'sequence':
          return '#'.repeat(Math.max(1, part.length ?? 5));
        case 'random':
          return '?'.repeat(Math.max(1, part.length ?? 6));
        case 'field': {
          const ref = part.fieldId ? byId.get(part.fieldId) : undefined;
          const v = ref?.values[row];
          if (!v || (!v.code && !v.value)) return '';
          hasResolvedPart = true;
          return part.source === 'value' ? v.value : v.code || v.value;
        }
        default:
          return '';
      }
    })
    .join('');
  // Only show a preview once at least one field/date part has real data for the row.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the flag is set inside the map callback, which TS's flow analysis does not follow
  return hasResolvedPart ? out : '';
}
